import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ResultPage from './ResultPage';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import RecategorizationPDF from './RecategorizationPDF';

type TDownloadButtonProps = {
    loading: boolean;
    setShowResult: React.Dispatch<React.SetStateAction<boolean>>;
}

type TRecategorizationWrapperProps = {
    setShowResult: React.Dispatch<React.SetStateAction<boolean>>
    modalRecategorizeClose: () => void
    payload: TRecategorizePayload
    recategorizationResponse: RecategorizationResponse
}


const DownloadButton = ({ loading, setShowResult }: TDownloadButtonProps) => (
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-white">
        {loading ? (
            <>
                <svg className="tw-animate-spin tw-h-5 tw-w-5" viewBox="0 0 24 24">
                    <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span>Generating PDF...</span>
            </>
        ) : (
            <>
                <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download PDF and Close</span>
            </>
        )}
    </div>
);

const RecategorizationWrapper = ({ setShowResult, modalRecategorizeClose, payload, recategorizationResponse }: TRecategorizationWrapperProps) => {

    const [attributes, setAttributes] = useState<TOption[]>()    

    const extractAndComposeAttributeList = () => {
        const getAttributesToAddValues = recategorizationResponse?.unique_attr_target_list?.filter((row, index) => {
            return !payload.unique_attr_source_list.find(
                attr => attr.target_attribute_id === row?.attribute_id && attr.checked
            );
        }).map(x => {
            return {
                id: x?.attribute_id, 
                name: x?.attribute_name
            }
        })
        setAttributes(getAttributesToAddValues)
    }

    useEffect(() => {
        extractAndComposeAttributeList()
    }, [payload?.unique_attr_source_list])

    const [loading, setLoading] = useState<boolean>(false);
    const pdfRef = useRef<HTMLDivElement>(null);

    const handleGenerateAndDownload = async () => {
        setLoading(true);

        try {
            const content = pdfRef.current;
            if (!content) return;

            const originalStyle = content.style.display;
            content.style.display = 'block';

            const canvas = await html2canvas(content, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: true,
                onclone: (document, element) => {
                    element.style.display = 'block';
                }
            });

            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');
            pdf.addImage(
                canvas.toDataURL('image/png'),
                'PNG',
                0,
                0,
                imgWidth,
                imgHeight
            );

            pdf.save(`recategorization_${new Date().toISOString().split('T')[0]}.pdf`);
            content.style.display = originalStyle;
            toast.success("Recategorization results downloaded successfully")
            setShowResult(false);
            modalRecategorizeClose()
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tw-relative">
            <ResultPage attributes={attributes as TOption[]}
            />
            <div className="pdf-container" style={{ position: 'absolute', left: '-9999px' }}>
                <div 
                    ref={pdfRef}
                    style={{
                        width: '800px',
                        background: 'white',
                        padding: '40px',
                        display: 'none'
                    }}
                >
                    <RecategorizationPDF
                        sourceName={payload?.source_category_name as string}
                        targetName={payload?.target_category_name as string}
                        totalProducts={payload?.product_list?.length as number}
                        attributes={attributes as TOption[]}
                    />
                </div>
            </div>
            <div className="tw-fixed tw-bottom-6 tw-right-6 tw-z-50">
                <button
                    onClick={handleGenerateAndDownload}
                    disabled={loading}
                    className={`tw-px-6 tw-py-2.5 tw-rounded-lg tw-font-medium ${
                        loading 
                            ? 'tw-bg-blue-300' 
                            : 'tw-bg-blue-700 hover:tw-bg-blue-800'
                    } tw-text-white tw-flex tw-items-center tw-gap-2 tw-shadow-lg hover:tw-shadow-xl tw-transition-all`}
                >
                    <DownloadButton loading={loading} setShowResult={setShowResult} />
                </button>
            </div>
        </div>
    );
};

export default RecategorizationWrapper;